import { z } from 'zod';
import {
  ERP_HOSTING,
  ERP_SYNC_DATA,
  ERP_SYSTEMS,
  ERP_SYSTEM_NAMES,
} from 'modules/Common/config/erpSystems';
import { isWhitelabelBrand } from 'modules/Common/hooks/useBranding';

// ERP integration request form contract (backend docs/features/erp-integration-requests).

// D2: white-label brands that offer the page besides the main instance. Empty
// for now; add e.g. 'ayming' together with the backend's
// ERP_INTEGRATION_WHITELABELS when its pilot starts.
export const ERP_INTEGRATION_WHITELABELS = [];

export const isErpIntegrationAvailable = (brandKey) =>
  !isWhitelabelBrand({ key: brandKey }) || ERP_INTEGRATION_WHITELABELS.includes(brandKey);

export const erpSystemLabel = (erpSystem, t) =>
  ERP_SYSTEM_NAMES[erpSystem] ?? t('app:erpIntegration.erp.other', 'Other');

export const buildErpIntegrationSchema = (t) =>
  z
    .object({
      erpSystem: z.enum(ERP_SYSTEMS, {
        errorMap: () => ({ message: t('app:erpIntegration.errors.erpSystem', 'Choose your ERP') }),
      }),
      erpSystemOther: z.string().trim().max(200).optional(),
      version: z.string().trim().max(100).optional(),
      hosting: z.enum(ERP_HOSTING, {
        errorMap: () => ({
          message: t('app:erpIntegration.errors.hosting', 'Choose how your ERP is hosted'),
        }),
      }),
      dataToSync: z
        .array(z.enum(ERP_SYNC_DATA))
        .min(1, t('app:erpIntegration.errors.dataToSync', 'Pick at least one kind of data')),
      contactName: z.string().trim().max(200).optional(),
      contactEmail: z
        .union([
          z
            .string()
            .trim()
            .email(t('app:erpIntegration.errors.contactEmail', 'Enter a valid email address')),
          z.literal(''),
        ])
        .optional(),
      note: z.string().trim().max(2000).optional(),
    })
    .superRefine((value, ctx) => {
      if (value.erpSystem === 'other' && !value.erpSystemOther) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['erpSystemOther'],
          message: t('app:erpIntegration.errors.erpSystemOther', 'Name your ERP system'),
        });
      }
    });

export const EMPTY_ERP_REQUEST = {
  erpSystem: '',
  erpSystemOther: '',
  version: '',
  hosting: '',
  dataToSync: [],
  contactName: '',
  contactEmail: '',
  note: '',
};

// Saved request → form values (absent optionals become empty inputs).
export const toFormValues = (request) =>
  request
    ? Object.fromEntries(
        Object.keys(EMPTY_ERP_REQUEST).map((key) => [key, request[key] ?? EMPTY_ERP_REQUEST[key]]),
      )
    : EMPTY_ERP_REQUEST;

// Form values → PUT body: blank optional answers are left out rather than
// stored as empty strings.
export const toRequestBody = (values) =>
  Object.fromEntries(
    Object.entries(values).filter(([key, value]) => {
      if (key === 'erpSystemOther' && values.erpSystem !== 'other') return false;
      return typeof value !== 'string' || value.trim() !== '';
    }),
  );
